import { NO, YES } from "uicore-ts"
import { RHWorkflowRecording, RHWorkflowRecordingRuntime } from "../Core/RHWorkflowRecordingRuntime"


export interface RHWorkflowRecordingPanelInterface {
    open: () => void
    close: () => void
    toggle: () => void
    refresh: () => void
}

declare global {
    interface Window {
        RHWorkflowRecordingPanel?: RHWorkflowRecordingPanelInterface
    }
}


export class RHWorkflowRecordingPanel {
    
    static _isInstalled = NO
    static _isCollapsed = NO
    static _panelElement: HTMLDivElement | null = null
    static _selectedRecordingID = ""
    static _panelLeft = -1
    static _panelTop = -1
    static _dragPointerID = -1
    static _dragOffsetX = 0
    static _dragOffsetY = 0
    static _fileSaveMessage = ""
    static _fileSaveMessageColor = "#94a3b8"
    static _keyboardHandler = (event: KeyboardEvent) => RHWorkflowRecordingPanel._keyboardEventWasReceived(event)
    static _pointerMoveHandler = (event: PointerEvent) => RHWorkflowRecordingPanel._pointerMoveWasReceived(event)
    static _pointerUpHandler = (event: PointerEvent) => RHWorkflowRecordingPanel._pointerUpWasReceived(event)
    static _recordingEventHandler = () => RHWorkflowRecordingPanel._recordingEventWasReceived()
    static readonly _styleElementID = "RHWorkflowRecordingPanelStyle"
    
    static install() {
        
        if (RHWorkflowRecordingPanel._isInstalled) {
            return
        }
        
        RHWorkflowRecordingPanel._isInstalled = YES
        RHWorkflowRecordingRuntime.install()
        RHWorkflowRecordingPanel._installStyleElement()
        
        window.RHWorkflowRecordingPanel = {
            open: () => RHWorkflowRecordingPanel.open(),
            close: () => RHWorkflowRecordingPanel.close(),
            toggle: () => RHWorkflowRecordingPanel.toggle(),
            refresh: () => RHWorkflowRecordingPanel.refresh()
        }
        
        // project-rules-ignore no-add-event-listener
        document.addEventListener("keydown", RHWorkflowRecordingPanel._keyboardHandler, YES)
        document.addEventListener("pointermove", RHWorkflowRecordingPanel._pointerMoveHandler, YES) // project-rules-ignore no-add-event-listener
        document.addEventListener("pointerup", RHWorkflowRecordingPanel._pointerUpHandler, YES) // project-rules-ignore no-add-event-listener
        window.addEventListener("RHWorkflowRecordingRuntime:eventRecorded", RHWorkflowRecordingPanel._recordingEventHandler) // project-rules-ignore no-add-event-listener
        window.addEventListener("RHWorkflowRecordingRuntime:recordingDidChange", RHWorkflowRecordingPanel._recordingEventHandler) // project-rules-ignore no-add-event-listener
        
    }
    
    static open() {
        
        RHWorkflowRecordingPanel._ensurePanel()
        RHWorkflowRecordingPanel.refresh()
        
    }
    
    static close() {
        
        RHWorkflowRecordingPanel._panelElement?.remove()
        RHWorkflowRecordingPanel._panelElement = null
        
    }
    
    static toggle() {
        
        if (RHWorkflowRecordingPanel._panelElement) {
            RHWorkflowRecordingPanel.close()
            return
        }
        
        RHWorkflowRecordingPanel.open()
        
    }
    
    static refresh() {
        
        const panelElement = RHWorkflowRecordingPanel._panelElement
        if (!panelElement) {
            return
        }
        
        panelElement.innerHTML = ""
        RHWorkflowRecordingPanel._applyPanelShellStyle()
        if (RHWorkflowRecordingPanel._isCollapsed === YES) {
            panelElement.appendChild(RHWorkflowRecordingPanel._compactElement())
            return
        }
        
        panelElement.appendChild(RHWorkflowRecordingPanel._headerElement())
        panelElement.appendChild(RHWorkflowRecordingPanel._statusElement())
        panelElement.appendChild(RHWorkflowRecordingPanel._controlsElement())
        panelElement.appendChild(RHWorkflowRecordingPanel._bodyElement())
        
    }
    
    static collapse() {
        
        RHWorkflowRecordingPanel._isCollapsed = YES
        RHWorkflowRecordingPanel.refresh()
        
    }
    
    static expand() {
        
        RHWorkflowRecordingPanel._isCollapsed = NO
        RHWorkflowRecordingPanel.refresh()
        
    }
    
    static _keyboardEventWasReceived(event: KeyboardEvent) {
        
        if (event.ctrlKey !== YES) {
            return
        }
        if (event.altKey === YES) {
            return
        }
        if (event.shiftKey === YES) {
            return
        }
        if (event.key.toLowerCase() !== "o") {
            return
        }
        
        event.preventDefault()
        event.stopPropagation()
        RHWorkflowRecordingPanel.toggle()
        
    }
    
    static _recordingEventWasReceived() {
        
        if (!RHWorkflowRecordingPanel._panelElement) {
            return
        }
        if (RHWorkflowRecordingPanel._isCollapsed !== YES) {
            return
        }
        
        RHWorkflowRecordingPanel.refresh()
        
    }
    
    static _installStyleElement() {
        
        if (document.getElementById(RHWorkflowRecordingPanel._styleElementID)) {
            return
        }
        
        const styleElement = document.createElement("style")
        styleElement.id = RHWorkflowRecordingPanel._styleElementID
        styleElement.textContent = `
#RHWorkflowRecordingPanel .rh-workflow-recorder-button {
    transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease, transform 80ms ease, opacity 120ms ease;
}
#RHWorkflowRecordingPanel .rh-workflow-recorder-button:hover:not(:disabled) {
    background: #334155 !important;
    border-color: #60a5fa !important;
}
#RHWorkflowRecordingPanel .rh-workflow-recorder-button:active:not(:disabled) {
    background: #1d4ed8 !important;
    border-color: #93c5fd !important;
    transform: translateY(1px);
}
#RHWorkflowRecordingPanel .rh-workflow-recorder-button:focus-visible {
    outline: 2px solid #93c5fd;
    outline-offset: 2px;
}
#RHWorkflowRecordingPanel .rh-workflow-recorder-button:disabled {
    cursor: default !important;
    opacity: 0.45;
}
#RHWorkflowRecordingPanel .rh-workflow-recorder-drag-handle {
    cursor: move;
    touch-action: none;
    user-select: none;
}
`
        document.head.appendChild(styleElement)
        
    }
    
    static _pointerMoveWasReceived(event: PointerEvent) {
        
        if (event.pointerId !== RHWorkflowRecordingPanel._dragPointerID) {
            return
        }
        
        RHWorkflowRecordingPanel._positionPanelAtLeftAndTop(
            event.clientX - RHWorkflowRecordingPanel._dragOffsetX,
            event.clientY - RHWorkflowRecordingPanel._dragOffsetY
        )
        event.preventDefault()
        event.stopPropagation()
        
    }
    
    static _pointerUpWasReceived(event: PointerEvent) {
        
        if (event.pointerId !== RHWorkflowRecordingPanel._dragPointerID) {
            return
        }
        
        RHWorkflowRecordingPanel._dragPointerID = -1
        event.preventDefault()
        event.stopPropagation()
        
    }
    
    static _dragWasStartedWithEvent(event: PointerEvent) {
        
        if (event.button !== 0) {
            return
        }
        
        const eventTarget = event.target
        if (eventTarget instanceof HTMLElement && eventTarget.closest("button, input, textarea, select, a")) {
            return
        }
        
        const panelElement = RHWorkflowRecordingPanel._panelElement
        if (!panelElement) {
            return
        }
        
        const panelRectangle = panelElement.getBoundingClientRect()
        RHWorkflowRecordingPanel._panelLeft = panelRectangle.left
        RHWorkflowRecordingPanel._panelTop = panelRectangle.top
        RHWorkflowRecordingPanel._dragOffsetX = event.clientX - panelRectangle.left
        RHWorkflowRecordingPanel._dragOffsetY = event.clientY - panelRectangle.top
        RHWorkflowRecordingPanel._dragPointerID = event.pointerId
        RHWorkflowRecordingPanel._applyPanelShellStyle()
        event.preventDefault()
        event.stopPropagation()
        
    }
    
    static _positionPanelAtLeftAndTop(left: number, top: number) {
        
        const panelElement = RHWorkflowRecordingPanel._panelElement
        if (!panelElement) {
            return
        }
        
        const panelRectangle = panelElement.getBoundingClientRect()
        const minimumInset = 14
        const maximumLeft = Math.max(minimumInset, window.innerWidth - panelRectangle.width - minimumInset)
        const maximumTop = Math.max(minimumInset, window.innerHeight - panelRectangle.height - minimumInset)
        
        RHWorkflowRecordingPanel._panelLeft = Math.min(Math.max(minimumInset, left), maximumLeft)
        RHWorkflowRecordingPanel._panelTop = Math.min(Math.max(minimumInset, top), maximumTop)
        RHWorkflowRecordingPanel._applyPanelShellStyle()
        
    }
    
    static _ensurePanel() {
        
        if (RHWorkflowRecordingPanel._panelElement) {
            return
        }
        
        const panelElement = document.createElement("div")
        panelElement.id = "RHWorkflowRecordingPanel"
        
        document.body.appendChild(panelElement)
        RHWorkflowRecordingPanel._panelElement = panelElement
        RHWorkflowRecordingPanel._applyPanelShellStyle()
        
    }
    
    static _applyPanelShellStyle() {
        
        const panelElement = RHWorkflowRecordingPanel._panelElement
        if (!panelElement) {
            return
        }
        
        let width = "min(940px, calc(100vw - 28px))"
        let maxHeight = "calc(100vh - 28px)"
        if (RHWorkflowRecordingPanel._isCollapsed === YES) {
            width = "min(360px, calc(100vw - 28px))"
            maxHeight = "none"
        }
        const positionParts: string[] = []
        if (RHWorkflowRecordingPanel._panelLeft >= 0 && RHWorkflowRecordingPanel._panelTop >= 0) {
            positionParts.push(`left: ${RHWorkflowRecordingPanel._panelLeft}px`)
            positionParts.push(`top: ${RHWorkflowRecordingPanel._panelTop}px`)
        }
        else {
            positionParts.push("right: 14px")
            positionParts.push("top: 14px")
        }
        
        panelElement.style.cssText = [
            "position: fixed",
            ...positionParts,
            `width: ${width}`,
            `max-height: ${maxHeight}`,
            "z-index: 2147483647",
            "background: #111827",
            "color: #f8fafc",
            "font: 12px/1.4 -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            "border: 1px solid rgba(148, 163, 184, 0.45)",
            "box-shadow: 0 24px 60px rgba(0, 0, 0, 0.42)",
            "border-radius: 8px",
            "overflow: hidden"
        ].join("; ")
        
    }
    
    static _compactElement(): HTMLElement {
        
        const recording = RHWorkflowRecordingRuntime.recording()
        const isRecording = RHWorkflowRecordingRuntime.isRecording()
        let statusText = "Idle"
        let statusColor = "#94a3b8"
        if (isRecording === YES) {
            statusText = "Recording"
            statusColor = "#f87171"
        }
        
        const compactElement = RHWorkflowRecordingPanel._element("div", [
            "display: grid",
            "grid-template-columns: auto minmax(0, 1fr) auto auto",
            "gap: 8px",
            "align-items: center",
            "padding: 9px 10px",
            "background: #0f172a"
        ])
        compactElement.setAttribute("data-rh-recorder-compact", "1")
        RHWorkflowRecordingPanel._makeElementDragHandle(compactElement)
        
        const statusDotElement = RHWorkflowRecordingPanel._element("div", [
            "width: 9px",
            "height: 9px",
            "border-radius: 999px",
            `background: ${statusColor}`,
            "box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.12)"
        ])
        const labelElement = RHWorkflowRecordingPanel._element("div", [
            "min-width: 0",
            "font-weight: 700",
            "overflow: hidden",
            "text-overflow: ellipsis",
            "white-space: nowrap"
        ], `${statusText} - ${recording.events.length} events`)
        
        let recordButton = RHWorkflowRecordingPanel._buttonElement("Start", () => {
            RHWorkflowRecordingRuntime.beginRecording(recording.name)
            RHWorkflowRecordingPanel.refresh()
        })
        if (isRecording === YES) {
            recordButton = RHWorkflowRecordingPanel._buttonElement("Stop", () => {
                RHWorkflowRecordingRuntime.finishRecording()
                RHWorkflowRecordingPanel.refresh()
            })
        }
        
        compactElement.append(
            statusDotElement,
            labelElement,
            recordButton,
            RHWorkflowRecordingPanel._buttonElement("Expand", () => RHWorkflowRecordingPanel.expand())
        )
        return compactElement
        
    }
    
    static _headerElement(): HTMLElement {
        
        const headerElement = RHWorkflowRecordingPanel._element("div", [
            "display: flex",
            "align-items: center",
            "justify-content: space-between",
            "gap: 12px",
            "background: #0f172a",
            "border-bottom: 1px solid rgba(148, 163, 184, 0.35)",
            "padding: 10px 12px"
        ])
        RHWorkflowRecordingPanel._makeElementDragHandle(headerElement)
        
        const titleElement = RHWorkflowRecordingPanel._element("div", [
            "font-weight: 700",
            "font-size: 13px",
            "letter-spacing: 0"
        ], "Workflow Recorder")
        const subtitleElement = RHWorkflowRecordingPanel._element("span", [
            "color: #94a3b8",
            "font-weight: 500",
            "margin-left: 10px"
        ], "Ctrl+O")
        titleElement.appendChild(subtitleElement)
        
        const headerButtonsElement = RHWorkflowRecordingPanel._element("div", [
            "display: flex",
            "gap: 6px",
            "align-items: center"
        ])
        headerButtonsElement.append(
            RHWorkflowRecordingPanel._buttonElement("Collapse", () => RHWorkflowRecordingPanel.collapse()),
            RHWorkflowRecordingPanel._buttonElement("Close", () => RHWorkflowRecordingPanel.close())
        )
        
        headerElement.append(titleElement, headerButtonsElement)
        return headerElement
        
    }
    
    static _statusElement(): HTMLElement {
        
        const recording = RHWorkflowRecordingRuntime.recording()
        const isRecording = RHWorkflowRecordingRuntime.isRecording()
        let statusText = "Idle"
        let statusColor = "#94a3b8"
        if (isRecording === YES) {
            statusText = "Recording"
            statusColor = "#f87171"
        }
        
        const statusElement = RHWorkflowRecordingPanel._element("div", [
            "display: grid",
            "grid-template-columns: repeat(6, minmax(0, 1fr))",
            "gap: 8px",
            "padding: 10px 12px",
            "border-bottom: 1px solid rgba(148, 163, 184, 0.2)"
        ])
        
        statusElement.append(
            RHWorkflowRecordingPanel._metricElement("Status", statusText, statusColor),
            RHWorkflowRecordingPanel._metricElement("Name", recording.name || "workflow-recording"),
            RHWorkflowRecordingPanel._metricElement("Current events", String(recording.events.length)),
            RHWorkflowRecordingPanel._metricElement("Saved", String(RHWorkflowRecordingRuntime.savedRecordings().length)),
            RHWorkflowRecordingPanel._metricElement("Linked media", RHWorkflowRecordingPanel._externalTimelineText(recording)),
            RHWorkflowRecordingPanel._metricElement("Video comments", String(recording.externalComments.length))
        )
        
        return statusElement
        
    }
    
    static _controlsElement(): HTMLElement {
        
        const wrapperElement = RHWorkflowRecordingPanel._element("div", [
            "border-bottom: 1px solid rgba(148, 163, 184, 0.2)"
        ])
        const controlsElement = RHWorkflowRecordingPanel._element("div", [
            "display: grid",
            "grid-template-columns: minmax(180px, 1fr) repeat(7, auto)",
            "gap: 8px",
            "align-items: center",
            "padding: 10px 12px"
        ])
        
        const nameInput = document.createElement("input")
        nameInput.id = "RHWorkflowRecordingPanelNameInput"
        nameInput.value = RHWorkflowRecordingRuntime.recording().name || "workflow-recording"
        nameInput.style.cssText = [
            "height: 30px",
            "min-width: 0",
            "background: #020617",
            "color: #f8fafc",
            "border: 1px solid rgba(148, 163, 184, 0.35)",
            "border-radius: 6px",
            "padding: 0 9px",
            "font: inherit"
        ].join("; ")
        
        const startButton = RHWorkflowRecordingPanel._buttonElement("Start", () => {
            RHWorkflowRecordingPanel._fileSaveMessage = ""
            RHWorkflowRecordingRuntime.beginRecording(nameInput.value)
            RHWorkflowRecordingPanel.refresh()
        })
        startButton.disabled = RHWorkflowRecordingRuntime.isRecording()
        
        const stopButton = RHWorkflowRecordingPanel._buttonElement("Stop", () => {
            RHWorkflowRecordingRuntime.finishRecording()
            RHWorkflowRecordingPanel.refresh()
        })
        stopButton.disabled = RHWorkflowRecordingRuntime.isRecording() !== YES
        
        const saveButton = RHWorkflowRecordingPanel._buttonElement("Save", () => {
            return RHWorkflowRecordingPanel._saveCurrentRecordingWasRequested()
        })
        const downloadButton = RHWorkflowRecordingPanel._buttonElement("Download", () => {
            RHWorkflowRecordingRuntime.downloadRecording()
            RHWorkflowRecordingPanel.refresh()
        })
        const copyButton = RHWorkflowRecordingPanel._buttonElement("Copy JSON", () => {
            RHWorkflowRecordingPanel._copyRecordingToClipboard(RHWorkflowRecordingRuntime.recording())
        })
        const clearButton = RHWorkflowRecordingPanel._buttonElement("Clear Current", () => {
            RHWorkflowRecordingRuntime.clearRecording()
            RHWorkflowRecordingPanel.refresh()
        })
        const refreshButton = RHWorkflowRecordingPanel._buttonElement("Refresh", () => RHWorkflowRecordingPanel.refresh())
        
        controlsElement.append(
            nameInput,
            startButton,
            stopButton,
            saveButton,
            downloadButton,
            copyButton,
            clearButton,
            refreshButton
        )
        
        wrapperElement.appendChild(controlsElement)
        wrapperElement.appendChild(RHWorkflowRecordingPanel._recordingOptionsElement())
        if (RHWorkflowRecordingPanel._fileSaveMessage.length > 0) {
            wrapperElement.appendChild(RHWorkflowRecordingPanel._fileSaveStatusElement())
        }
        
        return wrapperElement
        
    }
    
    static _bodyElement(): HTMLElement {
        
        const bodyElement = RHWorkflowRecordingPanel._element("div", [
            "display: grid",
            "grid-template-columns: 320px minmax(0, 1fr)",
            "min-height: 360px",
            "max-height: calc(100vh - 190px)"
        ])
        
        bodyElement.append(
            RHWorkflowRecordingPanel._savedRecordingsElement(),
            RHWorkflowRecordingPanel._detailElement()
        )
        
        return bodyElement
        
    }
    
    static _recordingOptionsElement(): HTMLElement {
        
        const options = RHWorkflowRecordingRuntime.recordingOptions()
        const optionsElement = RHWorkflowRecordingPanel._element("div", [
            "display: flex",
            "flex-wrap: wrap",
            "gap: 10px",
            "align-items: center",
            "padding: 0 12px 10px",
            "color: #cbd5e1"
        ])
        
        optionsElement.append(
            RHWorkflowRecordingPanel._optionCheckboxElement(
                "Pointer detail",
                options.includePointerDetails,
                checked => RHWorkflowRecordingRuntime.updateRecordingOptions({ includePointerDetails: checked })
            ),
            RHWorkflowRecordingPanel._optionCheckboxElement(
                "Focus/blur",
                options.includeFocusEvents,
                checked => RHWorkflowRecordingRuntime.updateRecordingOptions({ includeFocusEvents: checked })
            ),
            RHWorkflowRecordingPanel._optionCheckboxElement(
                "Keyboard detail",
                options.includeKeyboardDetails,
                checked => RHWorkflowRecordingRuntime.updateRecordingOptions({ includeKeyboardDetails: checked })
            ),
            RHWorkflowRecordingPanel._optionCheckboxElement(
                "Duplicate controls",
                options.includeControlDuplicates,
                checked => RHWorkflowRecordingRuntime.updateRecordingOptions({ includeControlDuplicates: checked })
            )
        )
        
        return optionsElement
        
    }
    
    static _optionCheckboxElement(
        title: string,
        isChecked: boolean,
        checkedValueDidChange: (checked: boolean) => void
    ): HTMLElement {
        
        const labelElement = RHWorkflowRecordingPanel._element("label", [
            "display: inline-flex",
            "align-items: center",
            "gap: 5px",
            "font: inherit",
            "cursor: pointer",
            "user-select: none"
        ])
        const checkboxElement = document.createElement("input")
        checkboxElement.type = "checkbox"
        checkboxElement.checked = isChecked
        checkboxElement.onchange = () => {
            checkedValueDidChange(checkboxElement.checked)
            RHWorkflowRecordingPanel.refresh()
        }
        labelElement.append(
            checkboxElement,
            RHWorkflowRecordingPanel._element("span", [], title)
        )
        
        return labelElement
        
    }
    
    static _fileSaveStatusElement(): HTMLElement {
        
        return RHWorkflowRecordingPanel._element("div", [
            "padding: 0 12px 10px",
            `color: ${RHWorkflowRecordingPanel._fileSaveMessageColor}`,
            "font-weight: 650"
        ], RHWorkflowRecordingPanel._fileSaveMessage)
        
    }
    
    static async _saveCurrentRecordingWasRequested() {
        
        RHWorkflowRecordingPanel._fileSaveMessage = "Saving recording..."
        RHWorkflowRecordingPanel._fileSaveMessageColor = "#93c5fd"
        RHWorkflowRecordingPanel.refresh()
        
        try {
            const result = await RHWorkflowRecordingRuntime.saveCurrentRecordingToTestRecordings()
            RHWorkflowRecordingPanel._fileSaveMessage = `Saved ${result.relativePath}`
            RHWorkflowRecordingPanel._fileSaveMessageColor = "#86efac"
        }
        catch (error) {
            let errorMessage = "dev file endpoint unavailable"
            if (error instanceof Error) {
                errorMessage = error.message
            }
            RHWorkflowRecordingPanel._fileSaveMessage = `Saved in browser only; ${errorMessage}`
            RHWorkflowRecordingPanel._fileSaveMessageColor = "#fbbf24"
            console.warn("[RHWorkflowRecordingPanel] Could not write recording file.", error)
        }
        
        RHWorkflowRecordingPanel.refresh()
        
    }
    
    static async _saveRecordingToFileWasRequested(recording: RHWorkflowRecording) {
        
        RHWorkflowRecordingPanel._fileSaveMessage = "Saving recording file..."
        RHWorkflowRecordingPanel._fileSaveMessageColor = "#93c5fd"
        RHWorkflowRecordingPanel.refresh()
        
        try {
            const result = await RHWorkflowRecordingRuntime.saveRecordingToTestRecordings(recording)
            RHWorkflowRecordingPanel._fileSaveMessage = `Saved ${result.relativePath}`
            RHWorkflowRecordingPanel._fileSaveMessageColor = "#86efac"
        }
        catch (error) {
            let errorMessage = "dev file endpoint unavailable"
            if (error instanceof Error) {
                errorMessage = error.message
            }
            RHWorkflowRecordingPanel._fileSaveMessage = `Could not write file: ${errorMessage}`
            RHWorkflowRecordingPanel._fileSaveMessageColor = "#fbbf24"
            console.warn("[RHWorkflowRecordingPanel] Could not write saved recording file.", error)
        }
        
        RHWorkflowRecordingPanel.refresh()
        
    }
    
    static _savedRecordingsElement(): HTMLElement {
        
        const savedRecordingsElement = RHWorkflowRecordingPanel._element("div", [
            "border-right: 1px solid rgba(148, 163, 184, 0.2)",
            "overflow: auto"
        ])
        
        const barElement = RHWorkflowRecordingPanel._element("div", [
            "display: flex",
            "align-items: center",
            "justify-content: space-between",
            "gap: 8px",
            "padding: 10px 12px",
            "border-bottom: 1px solid rgba(148, 163, 184, 0.18)",
            "position: sticky",
            "top: 0",
            "background: #111827"
        ])
        barElement.append(
            RHWorkflowRecordingPanel._element("strong", [], "Saved recordings"),
            RHWorkflowRecordingPanel._buttonElement("Clear All", () => {
                RHWorkflowRecordingRuntime.clearSavedRecordings()
                RHWorkflowRecordingPanel._selectedRecordingID = ""
                RHWorkflowRecordingPanel.refresh()
            })
        )
        savedRecordingsElement.appendChild(barElement)
        
        const savedRecordings = RHWorkflowRecordingRuntime.savedRecordings()
        if (savedRecordings.length === 0) {
            savedRecordingsElement.appendChild(
                RHWorkflowRecordingPanel._emptyElement("No saved recordings yet. Stop a session, then use Save.")
            )
            return savedRecordingsElement
        }
        
        savedRecordings.forEach(recording => {
            savedRecordingsElement.appendChild(RHWorkflowRecordingPanel._savedRecordingRowElement(recording))
        })
        
        return savedRecordingsElement
        
    }
    
    static _savedRecordingRowElement(recording: RHWorkflowRecording): HTMLElement {
        
        const rowElement = RHWorkflowRecordingPanel._element("button", [
            "display: block",
            "width: 100%",
            "text-align: left",
            "border: 0",
            "border-bottom: 1px solid rgba(148, 163, 184, 0.16)",
            "background: transparent",
            "color: #e5e7eb",
            "padding: 10px 12px",
            "cursor: pointer",
            "font: inherit"
        ])
        rowElement.setAttribute("data-rh-recording-id", recording.id || "")
        if (recording.id === RHWorkflowRecordingPanel._selectedRecordingID) {
            rowElement.style.cssText += "; background: rgba(59, 130, 246, 0.22)"
        }
        
        const titleElement = RHWorkflowRecordingPanel._element("div", [
            "font-weight: 700",
            "overflow: hidden",
            "text-overflow: ellipsis",
            "white-space: nowrap"
        ], recording.name)
        const metaElement = RHWorkflowRecordingPanel._element("div", [
            "color: #94a3b8",
            "font-size: 11px",
            "margin-top: 3px"
        ], `${recording.events.length} events - ${RHWorkflowRecordingPanel._dateText(recording.finishedAt || recording.startedAt)}`)
        const countsElement = RHWorkflowRecordingPanel._element("div", [
            "color: #cbd5e1",
            "font-size: 11px",
            "margin-top: 4px"
        ], RHWorkflowRecordingPanel._eventCountsText(recording))
        
        rowElement.append(titleElement, metaElement, countsElement)
        rowElement.onclick = () => {
            RHWorkflowRecordingPanel._selectedRecordingID = recording.id || ""
            RHWorkflowRecordingPanel.refresh()
        }
        
        return rowElement
        
    }
    
    static _detailElement(): HTMLElement {
        
        const detailElement = RHWorkflowRecordingPanel._element("div", [
            "overflow: auto",
            "min-width: 0"
        ])
        
        const selectedRecording = RHWorkflowRecordingPanel._selectedRecording()
        if (!selectedRecording) {
            detailElement.appendChild(RHWorkflowRecordingPanel._currentRecordingElement())
            return detailElement
        }
        
        detailElement.appendChild(RHWorkflowRecordingPanel._recordingDetailElement(selectedRecording))
        return detailElement
        
    }
    
    static _currentRecordingElement(): HTMLElement {
        
        const currentRecording = RHWorkflowRecordingRuntime.recording()
        const wrapperElement = RHWorkflowRecordingPanel._element("div", ["padding: 12px"])
        wrapperElement.appendChild(RHWorkflowRecordingPanel._element("h3", [
            "margin: 0 0 8px",
            "font-size: 13px"
        ], "Current recording"))
        wrapperElement.appendChild(RHWorkflowRecordingPanel._recordingSummaryElement(currentRecording))
        wrapperElement.appendChild(RHWorkflowRecordingPanel._recordingPreviewElement(currentRecording))
        return wrapperElement
        
    }
    
    static _recordingDetailElement(recording: RHWorkflowRecording): HTMLElement {
        
        const wrapperElement = RHWorkflowRecordingPanel._element("div", ["padding: 12px"])
        const titleRowElement = RHWorkflowRecordingPanel._element("div", [
            "display: flex",
            "justify-content: space-between",
            "gap: 8px",
            "align-items: center",
            "margin-bottom: 8px"
        ])
        titleRowElement.appendChild(RHWorkflowRecordingPanel._element("h3", [
            "margin: 0",
            "font-size: 13px"
        ], recording.name))
        
        const actionRowElement = RHWorkflowRecordingPanel._element("div", [
            "display: flex",
            "gap: 6px",
            "align-items: center"
        ])
        actionRowElement.append(
            RHWorkflowRecordingPanel._buttonElement("Save File", () => {
                return RHWorkflowRecordingPanel._saveRecordingToFileWasRequested(recording)
            }),
            RHWorkflowRecordingPanel._buttonElement("Download", () => RHWorkflowRecordingPanel._downloadRecording(recording)),
            RHWorkflowRecordingPanel._buttonElement("Copy JSON", () => RHWorkflowRecordingPanel._copyRecordingToClipboard(recording)),
            RHWorkflowRecordingPanel._buttonElement("Delete", () => {
                if (recording.id) {
                    RHWorkflowRecordingRuntime.deleteSavedRecording(recording.id)
                }
                RHWorkflowRecordingPanel._selectedRecordingID = ""
                RHWorkflowRecordingPanel.refresh()
            })
        )
        titleRowElement.appendChild(actionRowElement)
        
        wrapperElement.appendChild(titleRowElement)
        wrapperElement.appendChild(RHWorkflowRecordingPanel._recordingSummaryElement(recording))
        wrapperElement.appendChild(RHWorkflowRecordingPanel._recordingPreviewElement(recording))
        return wrapperElement
        
    }
    
    static _recordingSummaryElement(recording: RHWorkflowRecording): HTMLElement {
        
        const summaryElement = RHWorkflowRecordingPanel._element("div", [
            "display: grid",
            "grid-template-columns: repeat(6, minmax(0, 1fr))",
            "gap: 8px",
            "margin-bottom: 10px"
        ])
        summaryElement.append(
            RHWorkflowRecordingPanel._metricElement("Started", RHWorkflowRecordingPanel._dateText(recording.startedAt)),
            RHWorkflowRecordingPanel._metricElement("Finished", RHWorkflowRecordingPanel._dateText(recording.finishedAt || "")),
            RHWorkflowRecordingPanel._metricElement("Events", String(recording.events.length)),
            RHWorkflowRecordingPanel._metricElement("Kinds", RHWorkflowRecordingPanel._eventCountsText(recording)),
            RHWorkflowRecordingPanel._metricElement("Linked media", RHWorkflowRecordingPanel._externalTimelineText(recording)),
            RHWorkflowRecordingPanel._metricElement("Video comments", String(recording.externalComments.length))
        )
        return summaryElement
        
    }
    
    static _recordingPreviewElement(recording: RHWorkflowRecording): HTMLElement {
        
        const previewElement = document.createElement("pre")
        previewElement.style.cssText = [
            "margin: 0",
            "background: #020617",
            "border: 1px solid rgba(148, 163, 184, 0.22)",
            "border-radius: 6px",
            "padding: 10px",
            "max-height: 420px",
            "overflow: auto",
            "white-space: pre-wrap",
            "word-break: break-word",
            "color: #dbeafe"
        ].join("; ")
        previewElement.textContent = JSON.stringify(recording, null, 2)
        return previewElement
        
    }
    
    static _selectedRecording(): RHWorkflowRecording | null {
        
        if (RHWorkflowRecordingPanel._selectedRecordingID.length === 0) {
            return null
        }
        
        const savedRecording = RHWorkflowRecordingRuntime.savedRecordings().find(
            recording => recording.id === RHWorkflowRecordingPanel._selectedRecordingID
        )
        if (savedRecording) {
            return savedRecording
        }
        
        return null
        
    }
    
    static _metricElement(label: string, value: string, valueColor?: string): HTMLElement {
        
        let displayValue = value
        if (displayValue.length === 0) {
            displayValue = "-"
        }
        
        const metricElement = RHWorkflowRecordingPanel._element("div", [
            "background: rgba(15, 23, 42, 0.72)",
            "border: 1px solid rgba(148, 163, 184, 0.18)",
            "border-radius: 6px",
            "padding: 7px 8px",
            "min-width: 0"
        ])
        const labelElement = RHWorkflowRecordingPanel._element("div", [
            "color: #94a3b8",
            "font-size: 10px",
            "text-transform: uppercase",
            "letter-spacing: 0"
        ], label)
        const valueElement = RHWorkflowRecordingPanel._element("div", [
            "font-weight: 700",
            "overflow: hidden",
            "text-overflow: ellipsis",
            "white-space: nowrap",
            `color: ${valueColor || "#f8fafc"}`
        ], displayValue)
        metricElement.append(labelElement, valueElement)
        return metricElement
        
    }
    
    static _emptyElement(text: string): HTMLElement {
        
        return RHWorkflowRecordingPanel._element("div", [
            "color: #94a3b8",
            "padding: 16px 12px"
        ], text)
        
    }
    
    static _makeElementDragHandle(element: HTMLElement) {
        
        element.classList.add("rh-workflow-recorder-drag-handle")
        element.setAttribute("data-rh-recorder-drag-handle", "1")
        element.onpointerdown = event => RHWorkflowRecordingPanel._dragWasStartedWithEvent(event)
        
    }
    
    static _buttonElement(title: string, action: () => void | Promise<void>): HTMLButtonElement {
        
        const buttonElement = document.createElement("button")
        buttonElement.classList.add("rh-workflow-recorder-button")
        buttonElement.textContent = title
        buttonElement.style.cssText = [
            "height: 30px",
            "border: 1px solid rgba(148, 163, 184, 0.35)",
            "background: #1e293b",
            "color: #f8fafc",
            "border-radius: 6px",
            "padding: 0 10px",
            "font: inherit",
            "font-weight: 650",
            "cursor: pointer",
            "white-space: nowrap"
        ].join("; ")
        buttonElement.onclick = () => {
            const result = action()
            if (result instanceof Promise) {
                result.catch(error => {
                    RHWorkflowRecordingPanel._fileSaveMessage = "Action failed"
                    RHWorkflowRecordingPanel._fileSaveMessageColor = "#fbbf24"
                    console.warn("[RHWorkflowRecordingPanel] Button action failed.", error)
                    RHWorkflowRecordingPanel.refresh()
                })
            }
        }
        return buttonElement
        
    }
    
    static _element(tagName: string, styleParts: string[], text?: string): HTMLElement {
        
        const element = document.createElement(tagName)
        element.style.cssText = styleParts.join("; ")
        if (typeof text === "string") {
            element.textContent = text
        }
        return element
        
    }
    
    static _eventCountsText(recording: RHWorkflowRecording): string {
        
        const countsByKind = new Map<string, number>()
        recording.events.forEach(event => {
            countsByKind.set(event.kind, (countsByKind.get(event.kind) || 0) + 1)
        })
        
        const parts: string[] = []
        countsByKind.forEach((count, kind) => {
            parts.push(`${kind}:${count}`)
        })
        
        if (parts.length === 0) {
            return "none"
        }
        
        return parts.join(" ")
        
    }
    
    static _externalTimelineText(recording: RHWorkflowRecording): string {
        
        if (!recording.externalTimeline) {
            return "None"
        }
        
        if (recording.externalTimeline.title && recording.externalTimeline.title.length > 0) {
            return recording.externalTimeline.title
        }
        if (recording.externalTimeline.videoUrl && recording.externalTimeline.videoUrl.length > 0) {
            return "Video linked"
        }
        if (recording.externalTimeline.googleDocUrl && recording.externalTimeline.googleDocUrl.length > 0) {
            return "Doc linked"
        }
        
        return recording.externalTimeline.id
        
    }
    
    static _dateText(value: string): string {
        
        if (value.length === 0) {
            return ""
        }
        
        try {
            return new Date(value).toLocaleString()
        }
        catch (error) {
            return value
        }
        
    }
    
    static _downloadRecording(recording: RHWorkflowRecording) {
        
        const blob = new Blob([JSON.stringify(recording, null, 2)], { type: "application/json" })
        const objectURL = URL.createObjectURL(blob)
        const anchorElement = document.createElement("a")
        anchorElement.href = objectURL
        anchorElement.download = `${RHWorkflowRecordingRuntime.fileSafeName(recording.name)}.json`
        anchorElement.click()
        URL.revokeObjectURL(objectURL)
        
    }
    
    static _copyRecordingToClipboard(recording: RHWorkflowRecording) {
        
        const text = JSON.stringify(recording, null, 2)
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).then(undefined)
            return
        }
        
        const textareaElement = document.createElement("textarea")
        textareaElement.value = text
        textareaElement.style.cssText = [
            "position: fixed",
            "left: -9999px",
            "top: -9999px"
        ].join("; ")
        document.body.appendChild(textareaElement)
        textareaElement.select()
        document.execCommand("copy")
        textareaElement.remove()
        
    }
    
}
